// El lienzo de un diseño — ADR-2026-09-17-un-diseno-es-un-chat y
// ADR-2026-09-17-el-lienzo-presta-tres-ranuras. Carga el chat del diseño (con eso
// `useLienzoDelChat` y el panel del Agente quedan parados en la fila correcta), suma al
// lienzo las imágenes propias como tandas de una celda con `url`, esconde las quitadas
// (que siguen en la biblioteca del diseño), y le presta al lienzo la barra, el adorno,
// el click y la capa del modo. TODA generación pasa por `lanzar`: los botones, los
// paneles y las herramientas del agente son el mismo camino. La mezcla que se está
// armando vive acá, en memoria (ADR-2026-09-17-la-mezcla-no-se-guarda).

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { AlertCircle, ChevronLeft, Highlighter, ImagePlus } from 'lucide-react';
import SubscriptionLimitModal from '../../../../components/SubscriptionLimitModal';
import { ANCHO_LOGO_SIDEBAR_MINIMA, useSidebar } from '../../../../context/SidebarContext';
import { useChatAtlas } from '../../../../chat-atlas/ChatAtlasContext';
import { useRegistrarPantalla, type DescripcionDePantalla, type ResultadoDeHerramientaDePantalla } from '../../../../chat-atlas';
import { LienzoDelAgente, type HerramientaDelModo } from '../../../../chat-atlas/lienzo/LienzoDelAgente';
import { useLienzoDelChat } from '../../../../chat-atlas/lienzo/useLienzoDelChat';
import type { PedidoGuardado, TandaGuardada } from '../../../../chat-atlas/lienzo/persistenciaDelLienzo';
import { consultarSaldo, tipoDeLimite, type TipoDeLimite } from '../../../../chat-atlas/lienzo/herramientas/saldo';
import type { TandaDelLienzo } from '../../../../chat-atlas/lienzo/tandasDelLienzo';
import { adjuntoDeSubida } from '../../../../chat-atlas/ui/bibliotecaDeAdjuntos';
import { BotonPreguntaleAtlas } from '../../../../chat-atlas/ui/BotonPreguntaleAtlas';
import type { ChatAttachment } from '../../agentAttachments';
import { BOTON_ESQUINA, BOTON_REDONDO_GRIS } from '../../../../chat-atlas/ui/estilosAgente';
import { useDatosDeCeldas } from '../../components/useDatosDeCeldas';
import { leerTituloDeChat, renombrarChat } from '../../agentChatApi';
import { useAgentEnv } from '../../agentEnvContext';
import { ratioDelFormatoElegido } from '../promptLibre/promptLibre';
import { ratioDeFormato } from '../../../../utils/ratioDeFormato';
import { hayQueCerrarElPanelAlSalir } from './accionesDeDiseno';
import { AdornoDeMezcla } from './AdornoDeMezcla';
import { BarraDeDiseno } from './BarraDeDiseno';
import {
  DisenoContext,
  type ImagenDeBiblioteca,
  type MezclaEnCurso,
  type OpcionesDeLanzar,
  type PanelAbierto,
  type ParteDeMezcla,
  type ResultadoDeLanzar,
  type ValorDeDiseno,
} from './disenoContext';
import { DISENO_VACIO, RESULTADO_DE_MEZCLA_DEFAULT, conQuitada, estaQuitada, sinQuitada, type AccionDeDiseno, type ResultadoDeMezcla } from './disenoGuardado';
import { ESTADO_BASE_DESCRITA, RUTA_DISENO } from './disenosApi';
import {
  firmarPropia,
  pedirFotosEnUso,
  pedirGeneracionDeDiseno,
  pedirMezcla,
  urlDeFilaCompleta,
  type PedidoLanzado,
} from './generarEnDiseno';
import { CLAVE_PANTALLA_DISENO, HERRAMIENTAS_DE_DISENO, SKILL_DE_DISENO } from './herramientasDeDiseno';
import { LineasDeMezcla } from './LineasDeMezcla';
import { HojaAgregarImagen } from './paneles/HojaAgregarImagen';
import { PanelDeAccion } from './paneles/PanelDeAccion';
import { PanelMezclar } from './paneles/PanelMezclar';
import { TituloDeDiseno } from './TituloDeDiseno';
import {
  CANTIDAD_POR_ACCION,
  cantidadParaAccion,
  formatoParaAccion,
  MAX_PARTES,
  promptBaseDescrita,
  promptPlanoTecnico,
  tituloDeTandaDeDiseno,
} from './promptsDeDiseno';
import { useUrlsDeFilas } from './useUrlsDeFilas';
import { useUrlsPropias } from './useUrlsPropias';

const PANTALLA = 'Diseño de indumentaria';

/** Lo que el lienzo sabe de cada celda de la biblioteca, esté o no a la vista. */
interface CeldaConocida {
  tandaId: string;
  imageId: string | null;
  url?: string;
  titulo: string;
}

export const LienzoDeDiseno: React.FC<{ disenoId: string }> = ({ disenoId }) => {
  const chat = useChatAtlas();
  const seleccionDelLienzo = chat.seleccionDelLienzo;
  const { companyId, userId } = useAgentEnv();
  const navigate = useNavigate();
  const location = useLocation();
  const { setSidebarMinima, pedirColapsarSidebar, sidebarMinima: tiraDeSidebar } = useSidebar();

  // ── 1. El chat del diseño es el chat actual (ADR-2026-09-17-un-diseno-es-un-chat).
  const chatActual = chat.motor.getChatId();
  const esElChat = chatActual === disenoId;
  const cargarChat = chat.cargarChat;
  useEffect(() => {
    if (chat.motor.getChatId() === disenoId) return;
    void cargarChat(disenoId)
      .then(() => {
        // Un id que no existe o es de otra empresa (RLS devuelve nada): el motor no cambia
        // de chat. A la portada, con aviso (spec §5).
        if (chat.motor.getChatId() !== disenoId) {
          navigate(RUTA_DISENO, { replace: true, state: { aviso: 'Ese diseño no existe o no es de tu empresa.' } });
        }
      })
      // Un error de red tampoco cambia de chat: sin este catch la pantalla se queda con
      // el header solo (esElChat en false) y nada que hacer. Mismo destino que el id
      // inválido, con un aviso propio.
      .catch(() => {
        navigate(RUTA_DISENO, { replace: true, state: { aviso: 'No se pudo abrir el diseño. Intentá de nuevo.' } });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disenoId]);

  // ── 2. La sidebar a la tira mientras el lienzo está a la vista, como el Agente.
  useEffect(() => {
    setSidebarMinima(true);
    pedirColapsarSidebar();
    return () => setSidebarMinima(false);
  }, [setSidebarMinima, pedirColapsarSidebar]);

  // ── 2b. Y el panel del agente se va con el lienzo (ver `hayQueCerrarElPanelAlSalir`).
  //      Por REF y con deps vacías, por dos motivos: tiene que correr al DESMONTAR y no
  //      al cambiar de diseño —pasar de un diseño a otro no desmonta este componente, y
  //      ahí el panel se queda donde está—; y lo que se mira es el estado del momento de
  //      SALIR, no el del render en que se armó el efecto.
  const alSalirRef = useRef<() => void>(() => {});
  alSalirRef.current = () => {
    if (
      hayQueCerrarElPanelAlSalir({
        abierto: chat.abierto,
        vista: chat.vista,
        chatId: chat.motor.getChatId(),
        disenoId,
      })
    ) {
      chat.cerrarChat();
    }
  };
  useEffect(() => () => alSalirRef.current(), []);

  // ── 3. El estado del lienzo (UNA instancia) y el diseño adentro.
  const estado = useLienzoDelChat();
  const diseno = estado.lienzo.diseno ?? DISENO_VACIO;
  const setDiseno = estado.setDiseno;
  const { urls: urlsPropias, ratios: ratiosPropios } = useUrlsPropias(diseno.imagenes);

  // ── 4. Las tandas: propias primero (así las derivadas encuentran su origen), después
  //      las del chat y las guardadas. `todas` es la biblioteca; `tandas`, lo que se ve
  //      (sin las quitadas — ADR-2026-09-17-agregar-imagen-es-la-biblioteca-del-diseno).
  const todas = useMemo<TandaDelLienzo[]>(() => {
    const propias: TandaDelLienzo[] = diseno.imagenes.flatMap((i) => {
      const url = urlsPropias.get(i.id);
      if (!url) return [];
      return [{
        id: `img-${i.id}`,
        titulo: i.titulo,
        creadaEn: i.creadaEn,
        ratio: ratiosPropios[i.id] ?? 1,
        esVideo: false,
        celdas: [{ id: i.id, imageId: null, esVideo: false, url }],
      }];
    });
    return [...propias, ...estado.tandas];
  }, [diseno.imagenes, urlsPropias, ratiosPropios, estado.tandas]);
  const tandas = useMemo<TandaDelLienzo[]>(() => {
    if (diseno.quitadas.length === 0) return todas;
    return todas.flatMap((t) => {
      const celdas = t.celdas.filter((c) => !estaQuitada(diseno, c.id));
      return celdas.length === 0 ? [] : [{ ...t, celdas }];
    });
  }, [todas, diseno]);
  const estadoDelLienzo = useMemo(() => ({ ...estado, tandas }), [estado, tandas]);

  // Índices por celda: `conocidas` cubre la biblioteca entera (rol, título, miniatura);
  // `indice`, sólo lo que está en el lienzo (lo que se puede lanzar).
  const conocidas = useMemo(() => {
    const m = new Map<string, CeldaConocida>();
    for (const t of todas) for (const c of t.celdas) m.set(c.id, { tandaId: t.id, imageId: c.imageId, url: c.url, titulo: c.titulo ?? t.titulo });
    return m;
  }, [todas]);
  const indice = useMemo(() => {
    const m = new Map<string, CeldaConocida>();
    for (const t of tandas) for (const c of t.celdas) m.set(c.id, { tandaId: t.id, imageId: c.imageId, url: c.url, titulo: c.titulo ?? t.titulo });
    return m;
  }, [tandas]);
  const idsDeFilas = useMemo(() => [...conocidas.values()].flatMap((c) => (c.imageId ? [c.imageId] : [])), [conocidas]);
  const datosDeFilas = useDatosDeCeldas(idsDeFilas, companyId);
  const urlsDeFilas = useUrlsDeFilas(datosDeFilas, companyId);

  /** Una celda con imagen (propia o fila), no un recuadro en vuelo. Todas tienen las
   *  mismas acciones (ADR-2026-09-17-todas-las-imagenes-tienen-las-mismas-acciones). */
  const tieneImagen = useCallback(
    (id: string): boolean => {
      const c = conocidas.get(id);
      return !!c && (!!c.imageId || !!c.url);
    },
    [conocidas],
  );
  const tituloDe = useCallback((id: string) => conocidas.get(id)?.titulo ?? '', [conocidas]);
  const ratioDe = useCallback(
    (id: string): number => {
      const c = conocidas.get(id);
      if (!c) return 1;
      if (!c.imageId) return ratiosPropios[id] ?? 1;
      return ratioDeFormato(datosDeFilas.get(c.imageId)?.formato) || 0.8;
    },
    [conocidas, ratiosPropios, datosDeFilas],
  );
  /** La URL del ORIGINAL de una celda del lienzo, para mandarla de referencia. */
  const urlDeReferencia = useCallback(
    async (id: string): Promise<string> => {
      const c = indice.get(id);
      if (!c) throw new Error('Esa imagen ya no está en el lienzo.');
      if (c.imageId) {
        const u = companyId ? await urlDeFilaCompleta(companyId, datosDeFilas.get(c.imageId)?.fileName ?? null) : null;
        if (!u) throw new Error('No se pudo leer la imagen original.');
        return u;
      }
      const propia = diseno.imagenes.find((i) => i.id === id);
      const u = propia ? await firmarPropia(propia) : null;
      if (!u) throw new Error('No se pudo leer la imagen.');
      return u;
    },
    [indice, companyId, datosDeFilas, diseno.imagenes],
  );

  // ── 5. Errores y límite, como en el lienzo del Agente (portal al body).
  const [error, setError] = useState<string | null>(null);
  const [limite, setLimite] = useState<TipoDeLimite | null>(null);

  // ── 6. La mezcla en curso: en memoria, nunca en el diseño
  //      (ADR-2026-09-17-la-mezcla-no-se-guarda). Cambiar de diseño la suelta.
  const [mezcla, setMezclaEstado] = useState<MezclaEnCurso | null>(null);
  const [parteEnEdicion, setParteEnEdicion] = useState<string | null>(null);
  useEffect(() => {
    setMezclaEstado(null);
    setParteEnEdicion(null);
  }, [disenoId]);
  const setMezcla = useCallback((fn: (m: MezclaEnCurso | null) => MezclaEnCurso | null) => setMezclaEstado(fn), []);
  const mezclaRef = useRef(mezcla);
  mezclaRef.current = mezcla;
  // El Marcador de la barra (ADR-2026-09-21-el-marcador-de-la-mezcla): sólo existe con
  // una mezcla armándose; se apaga con ella.
  const [marcador, setMarcador] = useState(false);
  useEffect(() => {
    if (!mezcla) setMarcador(false);
  }, [mezcla]);
  useEffect(() => {
    if (!marcador) return;
    // En captura y cortando la propagación: ese Escape apaga el Marcador y nada más (la
    // hoja de Mezclar escucha Esc en `document` y cerraría la mezcla; el lienzo, en `window`).
    const alTeclado = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      setMarcador(false);
    };
    document.addEventListener('keydown', alTeclado, true);
    return () => document.removeEventListener('keydown', alTeclado, true);
  }, [marcador]);
  // La base con partes lleva "Generar mezcla" debajo, en el lugar del título.
  const sinPie = useCallback((id: string) => !!mezcla && mezcla.baseId === id && mezcla.partes.length > 0, [mezcla]);
  const herramientaDelModo = useMemo<HerramientaDelModo | null>(
    () => (mezcla ? { rotulo: 'Marcador', Icono: Highlighter, activa: marcador, onCambiar: setMarcador } : null),
    [mezcla, marcador],
  );

  // ── 7. LANZAR: el único camino de generación. `armarPedido` arma el pedido de cada
  //      acción (y lo que se guarda para poder rearmarlo); `lanzar` lo cuelga del lienzo;
  //      `relanzar` lo rearma desde lo guardado, con los mismos ids, cuando el backend
  //      nunca lo recibió (ADR-2026-09-18-una-tanda-en-vuelo-se-relanza-si-el-pedido-no-llego).
  const armarPedido = useCallback(
    async (
      accion: AccionDeDiseno,
      celdaId: string | null,
      opts: OpcionesDeLanzar,
      total: number,
      ids: { executionId?: string; executionIds?: string[] } = {},
    ): Promise<{ pedido: PedidoLanzado; guardado: PedidoGuardado; ratioPrenda: number } | { error: string }> => {
      if (!companyId || !userId) return { error: 'La cuenta todavía no cargó.' };
      try {
        // MOSTRAR EN USO es dos pasos (la visión escribe un prompt por foto y después un
        // pedido por prompt): lo arma `pedirFotosEnUso` y la tanda se reanuda por todos
        // sus ids (ADR-2026-09-18-mostrar-en-uso-escribe-sus-prompts). Los recuadros
        // aparecen ya: los ids se fijan antes de escribir
        // (ADR-2026-09-18-los-recuadros-aparecen-antes-que-el-prompt).
        if (accion === 'en-uso') {
          if (!celdaId || !indice.has(celdaId)) return { error: 'Esa imagen no está en el lienzo.' };
          const instruccion = opts.instruccion ?? '';
          const origen = await urlDeReferencia(celdaId);
          const pedido = pedirFotosEnUso({
            userId, companyId, urlPrenda: origen, instruccion, cantidad: total, executionIds: ids.executionIds,
          });
          return { pedido, ratioPrenda: 1, guardado: { accion, celdaId, instruccion, cantidad: total } };
        }
        // MEZCLAR también es dos pasos: la visión mira la base y las partes con lo que se
        // escribió de cada una y decide qué imagen es el resultado
        // (ADR-2026-09-18-mezclar-escribe-su-prompt); después, un pedido con todas las
        // imágenes como referencias, la base primero.
        if (accion === 'mezcla') {
          if (!celdaId || !indice.has(celdaId)) return { error: 'Esa imagen no está en el lienzo.' };
          const enCurso = mezclaRef.current;
          const partes = (opts.partes ?? (enCurso?.baseId === celdaId ? enCurso.partes : []))
            .filter((p) => indice.has(p.celdaId) && p.celdaId !== celdaId)
            .map((p) => ({ celdaId: p.celdaId, instruccion: p.instruccion.trim(), ...(p.marca ? { marca: p.marca } : {}) }));
          if (partes.length === 0) return { error: 'La mezcla no tiene partes: sumá texturas o inspiraciones.' };
          if (partes.length > MAX_PARTES) return { error: `Una mezcla acepta hasta ${MAX_PARTES} partes.` };
          const instruccionGeneral = opts.instruccionGeneral ?? (enCurso?.baseId === celdaId ? enCurso.instruccion : '');
          const resultado = opts.resultado ?? (enCurso?.baseId === celdaId ? enCurso.resultado : RESULTADO_DE_MEZCLA_DEFAULT);
          const marcaBase = opts.marcaBase ?? (enCurso?.baseId === celdaId ? enCurso.marcaBase : undefined);
          const ratioPrenda = ratioDe(celdaId);
          const urlBase = await urlDeReferencia(celdaId);
          const urls = await Promise.all(partes.map((p) => urlDeReferencia(p.celdaId)));
          const pedido = pedirMezcla({
            userId,
            companyId,
            urlBase,
            ...(marcaBase ? { marcaBase } : {}),
            partes: partes.map((p, i) => ({ url: urls[i], instruccion: p.instruccion, ...(p.marca ? { marca: p.marca } : {}) })),
            instruccionGeneral,
            resultado,
            cantidad: total,
            ratioDeLaPrenda: ratioPrenda,
            executionId: ids.executionId,
          });
          return {
            pedido,
            ratioPrenda,
            guardado: { accion, celdaId, partes, instruccionGeneral, resultado, ...(marcaBase ? { marcaBase } : {}), cantidad: total },
          };
        }
        let prompt: string;
        let referencias: string[];
        let ratioPrenda = 1;
        const textoBase = (opts.textoBase ?? '').trim();
        if (accion === 'base-descrita') {
          if (!textoBase) return { error: 'Falta la descripción de la prenda.' };
          prompt = promptBaseDescrita(textoBase);
          referencias = [];
        } else {
          if (!celdaId || !indice.has(celdaId)) return { error: 'Esa imagen no está en el lienzo.' };
          ratioPrenda = ratioDe(celdaId);
          prompt = promptPlanoTecnico();
          referencias = [await urlDeReferencia(celdaId)];
        }
        const pedido = pedirGeneracionDeDiseno({
          accion,
          userId,
          companyId,
          prompt,
          referencias,
          ratioDeLaPrenda: ratioPrenda,
          cantidad: total,
          fondoBlanco: accion === 'plano' || accion === 'base-descrita',
          executionId: ids.executionId,
        });
        return {
          pedido,
          ratioPrenda,
          guardado: { accion, ...(celdaId ? { celdaId } : {}), ...(textoBase ? { textoBase } : {}), cantidad: total },
        };
      } catch (e) {
        return { error: e instanceof Error ? e.message : 'No se pudo preparar el pedido.' };
      }
    },
    [companyId, userId, indice, ratioDe, urlDeReferencia],
  );
  const avisarSiFalla = useCallback((resultado: Promise<string[]>) => {
    resultado.catch((e: unknown) => {
      setError(e instanceof Error && e.message ? e.message : 'No se pudo generar. Intentá nuevamente.');
    });
  }, []);

  const lanzar = useCallback(
    async (accion: AccionDeDiseno, celdaId: string | null, opts: OpcionesDeLanzar = {}): Promise<ResultadoDeLanzar> => {
      if (!companyId || !userId) return { ok: false, motivo: 'La cuenta todavía no cargó.' };
      const total = cantidadParaAccion(accion, opts.cantidad);
      const saldo = await consultarSaldo(companyId);
      if (saldo.restantes !== null && saldo.restantes < total) {
        setLimite(tipoDeLimite(saldo.plan));
        return { ok: false, motivo: 'No hay créditos suficientes.' };
      }
      const armado = await armarPedido(accion, celdaId, opts, total);
      if ('error' in armado) return { ok: false, motivo: armado.error };
      const { pedido, guardado, ratioPrenda } = armado;
      const tandaId = estado.agregarTanda({
        titulo: tituloDeTandaDeDiseno(accion, celdaId ? tituloDe(celdaId) : ''),
        total: pedido.total,
        ratio: ratioDelFormatoElegido(formatoParaAccion(accion, ratioPrenda)),
        esVideo: false,
        ...(celdaId ? { origenId: celdaId } : {}),
        ...(pedido.executionIds.length > 1 ? { executionIds: pedido.executionIds } : { executionId: pedido.executionId }),
        pedido: guardado,
        resultado: pedido.resultado,
      });
      avisarSiFalla(pedido.resultado);
      return { ok: true, tandaId, total: pedido.total };
    },
    [companyId, userId, estado, tituloDe, armarPedido, avisarSiFalla],
  );

  // RELANZAR: la tanda quedó guardada con su pedido y el backend nunca lo recibió (la
  // página se recargó mientras la visión escribía el prompt). Se arma de nuevo con los
  // MISMOS ids: la tanda sigue siendo la misma y no se cobra dos veces. Sin saldo que
  // consultar: ya se consultó al pedirla, y el backend lo vuelve a controlar.
  const relanzar = useCallback(
    (t: TandaGuardada): Promise<string[]> | null => {
      const p = t.pendiente?.pedido;
      if (!p) return null;
      const opts: OpcionesDeLanzar = {
        instruccion: p.instruccion,
        partes: p.partes,
        instruccionGeneral: p.instruccionGeneral,
        resultado: p.resultado,
        marcaBase: p.marcaBase,
        textoBase: p.textoBase,
        cantidad: p.cantidad,
      };
      const executionId = t.pendiente?.executionId;
      const ids = {
        executionId,
        executionIds: t.pendiente?.executionIds ?? (executionId ? [executionId] : undefined),
      };
      return armarPedido(p.accion, p.celdaId ?? null, opts, p.cantidad, ids).then((armado) => {
        if ('error' in armado) throw new Error(armado.error);
        avisarSiFalla(armado.pedido.resultado);
        return armado.pedido.resultado;
      });
    },
    [armarPedido, avisarSiFalla],
  );
  const fijarRelanzador = estado.fijarRelanzador;
  useEffect(() => {
    fijarRelanzador(relanzar);
    return () => fijarRelanzador(null);
  }, [fijarRelanzador, relanzar]);

  // ── 8. La base descrita en la portada: se pide UNA vez al llegar.
  const baseDescritaPedidaRef = useRef(false);
  useEffect(() => {
    const texto = (location.state as Record<string, unknown> | null)?.[ESTADO_BASE_DESCRITA];
    if (!esElChat || typeof texto !== 'string' || baseDescritaPedidaRef.current || !companyId || !userId) return;
    baseDescritaPedidaRef.current = true;
    navigate(location.pathname, { replace: true, state: null });
    void lanzar('base-descrita', null, { textoBase: texto }).then((r) => {
      if (!r.ok) setError(r.motivo);
    });
  }, [esElChat, location, companyId, userId, lanzar, navigate]);

  // ── 9. Paneles, la mezcla, agregar y la biblioteca.
  const [panel, abrirPanel] = useState<PanelAbierto | null>(null);
  const [agregar, setAgregar] = useState<{ archivos: File[] } | null>(null);

  const alternarParte = useCallback(
    (id: string) => {
      const m = mezclaRef.current;
      if (!m || id === m.baseId) return;
      const yaEsParte = m.partes.some((p) => p.celdaId === id);
      if (!yaEsParte && m.partes.length >= MAX_PARTES) {
        setError(`Una mezcla acepta hasta ${MAX_PARTES} partes.`);
        return;
      }
      setMezclaEstado(
        yaEsParte
          ? { ...m, partes: m.partes.filter((p) => p.celdaId !== id) }
          : { ...m, partes: [...m.partes, { celdaId: id, instruccion: '' }] },
      );
      setParteEnEdicion(yaEsParte ? null : id);
    },
    [],
  );
  const alTocarCelda = useCallback(
    (id: string): boolean => {
      if (!mezclaRef.current || id === mezclaRef.current.baseId) return false;
      if (!tieneImagen(id)) return true;
      alternarParte(id);
      return true;
    },
    [tieneImagen, alternarParte],
  );
  const generarMezcla = useCallback(() => {
    const m = mezclaRef.current;
    if (!m) return;
    void lanzar('mezcla', m.baseId, { partes: m.partes, instruccionGeneral: m.instruccion, cantidad: m.cantidad, marcaBase: m.marcaBase }).then((r) => {
      if (!r.ok) {
        setError(r.motivo);
        return;
      }
      setParteEnEdicion(null);
      setMezclaEstado(null);
      abrirPanel(null);
    });
  }, [lanzar]);

  const quitarDelLienzo = useCallback(
    (id: string) => {
      if (!conocidas.has(id)) return;
      setDiseno((d) => conQuitada(d, id));
      // Si estaba en la mezcla, se va de ella; si ERA la prenda, la mezcla se cierra.
      setMezclaEstado((m) => (m ? (m.baseId === id ? null : { ...m, partes: m.partes.filter((p) => p.celdaId !== id) }) : m));
      if (mezclaRef.current?.baseId === id) abrirPanel(null);
    },
    [conocidas, setDiseno],
  );
  const devolverAlLienzo = useCallback((id: string) => setDiseno((d) => sinQuitada(d, id)), [setDiseno]);

  const agregarDeCreaciones = useCallback(
    (pick: { id: string; ratio: number; titulo: string }) => {
      if (conocidas.has(pick.id)) {
        setDiseno((d) => sinQuitada(d, pick.id));
        return;
      }
      estado.agregarTanda({ titulo: pick.titulo, total: 1, ratio: pick.ratio, esVideo: false, resultado: Promise.resolve([pick.id]) });
    },
    [conocidas, estado, setDiseno],
  );

  const celdas = useCallback(
    () => [...indice.keys()].filter(tieneImagen).map((id) => ({ id, titulo: tituloDe(id) })),
    [indice, tieneImagen, tituloDe],
  );
  const biblioteca = useCallback(
    (): ImagenDeBiblioteca[] =>
      [...conocidas.keys()].filter(tieneImagen).map((id) => ({ id, titulo: tituloDe(id), enLienzo: !estaQuitada(diseno, id) })),
    [conocidas, tieneImagen, tituloDe, diseno],
  );
  // La miniatura de una celda: la propia si nació con `url`, y si no la `opt/` firmada de
  // su fila. Sin esto los paneles dibujaban un hueco en cuanto la celda era una generada.
  const urlDe = useCallback(
    (id: string): string | undefined => {
      const c = conocidas.get(id);
      if (!c) return undefined;
      return c.url ?? (c.imageId ? urlsDeFilas.get(c.imageId) : undefined);
    },
    [conocidas, urlsDeFilas],
  );
  // El chip de una imagen propia, el mismo para "Agregar al chat" y para la selección del
  // lienzo (ADR-2026-09-24-la-seleccion-del-lienzo-se-adjunta-al-escribir).
  const adjuntoDePropia = useCallback(
    (id: string): ChatAttachment | null => {
      const propia = diseno.imagenes.find((i) => i.id === id);
      const url = urlsPropias.get(id);
      return propia && url ? adjuntoDeSubida({ storagePath: propia.path, previewUrl: url }) : null;
    },
    [diseno.imagenes, urlsPropias],
  );

  const valor = useMemo<ValorDeDiseno>(
    () => ({
      diseno, companyId, urlDe, tituloDe, adjuntoDePropia, celdas, biblioteca, lanzar, panel, abrirPanel, mezcla, setMezcla, alternarParte, generarMezcla, marcador, setMarcador,
      parteEnEdicion, setParteEnEdicion, setDiseno, quitarDelLienzo, devolverAlLienzo, agregarDeCreaciones,
      abrirAgregar: (archivos = []) => setAgregar({ archivos }),
      avisarError: setError,
    }),
    [diseno, companyId, urlDe, tituloDe, adjuntoDePropia, celdas, biblioteca, lanzar, panel, mezcla, setMezcla, alternarParte, generarMezcla, marcador, parteEnEdicion, setDiseno, quitarDelLienzo, devolverAlLienzo, agregarDeCreaciones],
  );

  // ── 10. Medida del contenedor: el lienzo recibe su ancho por prop, como en el Agente.
  const contenedorRef = useRef<HTMLDivElement | null>(null);
  const [ancho, setAncho] = useState(0);
  useLayoutEffect(() => {
    const el = contenedorRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const medir = () => setAncho(el.clientWidth);
    medir();
    const ro = new ResizeObserver(medir);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // El título del diseño es el del CHAT: la portada lo escribe ahí, y un diseño que nació
  // de la caja de prompt (una prenda descrita) no tiene imagen propia de la que sacarlo.
  // Se lee una vez por diseño; mientras tanto cae en la imagen propia, como antes.
  const [tituloDelChat, setTituloDelChat] = useState<string | null>(null);
  useEffect(() => {
    let vivo = true;
    setTituloDelChat(null);
    void leerTituloDeChat(disenoId).then((t) => {
      if (vivo) setTituloDelChat(t);
    });
    return () => {
      vivo = false;
    };
  }, [disenoId]);
  const titulo = tituloDelChat || diseno.imagenes[0]?.titulo || 'Diseño';
  const renombrar = useCallback(
    async (nuevo: string) => {
      const ok = await renombrarChat(disenoId, nuevo);
      if (ok) setTituloDelChat(nuevo);
      else setError('No se pudo cambiar el nombre. Probá de nuevo.');
      return ok;
    },
    [disenoId],
  );

  // ── 11. El agente ve y opera el lienzo (ADR-2026-09-10-el-agente-toca-la-pantalla,
  //       ADR-2026-09-17-el-agente-opera-el-lienzo-del-diseno): sin campos, con las
  //       tres acciones como herramientas que llaman a `lanzar`.
  const texto = (v: unknown): string => (typeof v === 'string' ? v : '');
  const numero = (v: unknown): number | undefined => (typeof v === 'number' && Number.isFinite(v) ? v : undefined);
  const ejecutarHerramientaDeDiseno = useCallback(
    async (nombre: string, input: unknown): Promise<ResultadoDeHerramientaDePantalla> => {
      const i = (input && typeof input === 'object' ? input : {}) as Record<string, unknown>;
      const celdaId = texto(i.celda_id);
      const cantidad = numero(i.cantidad);
      const fallo = (motivo: string): ResultadoDeHerramientaDePantalla => ({ content: motivo, is_error: true });
      const listo = (r: ResultadoDeLanzar, que: string): ResultadoDeHerramientaDePantalla =>
        r.ok
          ? { content: `Se pidieron ${r.total} ${que} de «${tituloDe(celdaId)}»; van a aparecer debajo en el lienzo en 30-60 s. No digas que ya están: el usuario las ve llegar.` }
          : fallo(r.motivo);
      switch (nombre) {
        case 'mostrar_en_uso': {
          return listo(await lanzar('en-uso', celdaId, { instruccion: texto(i.instruccion), cantidad }), 'fotos en uso');
        }
        case 'plano_tecnico': {
          return listo(await lanzar('plano', celdaId), 'plano técnico');
        }
        case 'mezclar': {
          const partes: ParteDeMezcla[] = (Array.isArray(i.partes) ? i.partes : [])
            .map((p) => (p && typeof p === 'object' ? (p as Record<string, unknown>) : {}))
            .map((p) => ({ celdaId: texto(p.celda_id), instruccion: texto(p.instruccion) }))
            .filter((p, k, arr) => p.celdaId && indice.has(p.celdaId) && p.celdaId !== celdaId && arr.findIndex((q) => q.celdaId === p.celdaId) === k);
          if (!indice.has(celdaId)) return fallo('No hay una celda con ese id en el lienzo.');
          if (partes.length === 0) return fallo('La mezcla necesita al menos una parte que esté en el lienzo.');
          if (partes.length > MAX_PARTES) return fallo(`Una mezcla acepta hasta ${MAX_PARTES} partes.`);
          const instruccion = texto(i.instruccion);
          const resultado: ResultadoDeMezcla = i.resultado === 'como_la_base' ? 'como-la-base' : RESULTADO_DE_MEZCLA_DEFAULT;
          if (i.generar === false) {
            // Queda armada EN PANTALLA (anillo, líneas y cajitas), para que el usuario la
            // revise y la genere; no se guarda (ADR-2026-09-17-la-mezcla-no-se-guarda).
            setMezclaEstado({ baseId: celdaId, partes, instruccion, cantidad: cantidadParaAccion('mezcla', cantidad), resultado });
            setParteEnEdicion(null);
            abrirPanel({ accion: 'mezcla', celdaId });
            return { content: `Quedó armada la mezcla de «${tituloDe(celdaId)}» con ${partes.length} parte(s); el usuario la ve en el lienzo y la genera con "Generar mezcla".` };
          }
          return listo(await lanzar('mezcla', celdaId, { partes, instruccionGeneral: instruccion || undefined, resultado, cantidad }), 'mezclas');
        }
        default:
          return fallo(`No conozco la herramienta ${nombre}.`);
      }
    },
    [lanzar, tituloDe, indice],
  );
  useRegistrarPantalla(
    useMemo(
      () => ({
        clave: CLAVE_PANTALLA_DISENO,
        describir: (): DescripcionDePantalla => {
          // Las seleccionadas van marcadas: "esta" o "la que tengo seleccionada" es ésa
          // (ADR-2026-09-24-la-seleccion-del-lienzo-se-adjunta-al-escribir).
          const seleccionadas = new Set(seleccionDelLienzo);
          const lista = celdas().map((c) => `${c.id} «${c.titulo || 'Imagen'}»${seleccionadas.has(c.id) ? ' [seleccionada]' : ''}`);
          const enCurso = mezcla
            ? `${mezcla.baseId}${mezcla.marcaBase ? ' [zona pintada: ahí va el cambio]' : ''} ← [${mezcla.partes.map((p) => `${p.celdaId}: "${p.instruccion}"${p.marca ? ' [zona pintada]' : ''}`).join(', ')}]${mezcla.instruccion ? ` (general: "${mezcla.instruccion}")` : ''}, ${mezcla.cantidad} imagen(es)`
            : 'ninguna';
          const enVuelo = estado.lienzo.tandas.filter((t) => t.pendiente).length;
          return {
            pantalla: `el lienzo de Diseño de indumentaria, diseño «${titulo}»`,
            ruta: `${RUTA_DISENO}/${disenoId}`,
            queHace: 'Un lienzo con imágenes (prendas, fotos en uso, inspiraciones, texturas), todas con las mismas tres acciones: mostrar en uso (un prompt por foto, escrito mirando la prenda), mezclar y plano técnico; los resultados caen debajo de la imagen.',
            campos: [],
            faltaParaGenerar: [],
            costo: `1 crédito por imagen: en uso ${CANTIDAD_POR_ACCION['en-uso']} y mezcla ${CANTIDAD_POR_ACCION.mezcla} por defecto (el usuario elige de 1 a 6), plano técnico 1.`,
            estado: `Celdas: ${lista.length > 0 ? lista.join('; ') : 'ninguna todavía'}. Seleccionadas: ${seleccionadas.size} (una seleccionada aparece apagada en la caja del chat y se adjunta al mensaje en cuanto el usuario escribe). Mezcla armándose: ${enCurso}. Tandas en vuelo: ${enVuelo}.`,
          };
        },
        aplicar: () => ({ aplicados: [], fallidos: [] }),
        herramientas: HERRAMIENTAS_DE_DISENO,
        ejecutar: ejecutarHerramientaDeDiseno,
        skill: SKILL_DE_DISENO,
      }),
      [celdas, seleccionDelLienzo, mezcla, estado.lienzo.tandas, titulo, disenoId, ejecutarHerramientaDeDiseno],
    ),
  );

  return (
    <DisenoContext.Provider value={valor}>
      <div className="flex h-[calc(100dvh-env(safe-area-inset-top)-4.25rem)] flex-col overflow-hidden md:h-[100dvh]">
        <header
          // Con la sidebar en tira, su logo flota en la esquina donde arranca este
          // encabezado: se le deja el lugar, como el encabezado del Agente.
          style={tiraDeSidebar ? { paddingLeft: ANCHO_LOGO_SIDEBAR_MINIMA } : undefined}
          className="flex h-16 shrink-0 items-center justify-between gap-3 border-b border-[rgb(var(--brand-black-rgb)_/_0.06)] px-4 transition-[padding] duration-300 ease-out"
        >
          <div className="flex min-w-0 items-center gap-3">
            <button type="button" onClick={() => navigate(RUTA_DISENO)} aria-label="Volver a las bases" title="Volver a las bases" className={BOTON_REDONDO_GRIS}>
              <ChevronLeft className="h-4 w-4" strokeWidth={2.25} aria-hidden="true" />
            </button>
            <div className="min-w-0 flex-1">
              <TituloDeDiseno
                variante="cabecera"
                titulo={titulo}
                onRenombrar={renombrar}
                sufijo={<span className="ml-2 text-[13px] font-medium text-[rgb(var(--brand-black-rgb)_/_0.45)]">{PANTALLA}</span>}
              />
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <button type="button" onClick={() => setAgregar({ archivos: [] })} className={BOTON_ESQUINA}>
              <ImagePlus className="h-4 w-4" strokeWidth={2.25} aria-hidden="true" />
              Agregar imagen
            </button>
            <BotonPreguntaleAtlas pantalla={PANTALLA} detalle={`diseño «${titulo}»`} variante="solido" />
          </div>
        </header>
        <div
          ref={contenedorRef}
          className="relative min-h-0 flex-1"
          onDragOver={(e) => {
            if (e.dataTransfer.types.includes('Files')) e.preventDefault();
          }}
          onDrop={(e) => {
            const archivos = Array.from(e.dataTransfer.files ?? []).filter((f) => f.type.startsWith('image/'));
            if (archivos.length === 0) return;
            e.preventDefault();
            setAgregar({ archivos });
          }}
        >
          {esElChat && ancho > 0 && (
            <LienzoDelAgente
              estado={estadoDelLienzo}
              ancho={ancho}
              Barra={BarraDeDiseno}
              Adorno={AdornoDeMezcla}
              alTocarCelda={alTocarCelda}
              sinPie={sinPie}
              Capa={LineasDeMezcla}
              adjuntoDePropia={adjuntoDePropia}
              herramientaDelModo={herramientaDelModo}
            />
          )}
        </div>
        {panel && panel.accion !== 'mezcla' && <PanelDeAccion key={`${panel.celdaId}:${panel.accion}`} panel={panel} />}
        {panel && panel.accion === 'mezcla' && <PanelMezclar key={panel.celdaId} baseId={panel.celdaId} />}
        {agregar && <HojaAgregarImagen archivosIniciales={agregar.archivos} onCerrar={() => setAgregar(null)} />}
        <SubscriptionLimitModal isOpen={limite !== null} onClose={() => setLimite(null)} type={limite ?? 'plan-limit-reached'} />
        {error &&
          createPortal(
            <button type="button" onClick={() => setError(null)} className="fixed bottom-6 left-1/2 z-[250] -translate-x-1/2 px-4">
              <span className="flex items-center gap-2 rounded-2xl bg-brand-black py-3 px-6 text-sm text-white shadow-xl">
                <AlertCircle className="h-4 w-4 shrink-0 text-red-400" />
                {error}
              </span>
            </button>,
            document.body,
          )}
      </div>
    </DisenoContext.Provider>
  );
};
